/**
 * Výběr tenanta pro Live instalaci — karty, ne native <select>.
 * U každého tenanta je jméno, slug, doména a beacon URL.
 */
import { useTranslation } from 'react-i18next';
import { Building2, Radio } from 'lucide-react';
import { Badge } from './ui/Badge';

export type LiveTenantOption = {
  slug: string;
  name: string;
  parent_id?: string | null;
  beacon_url?: string | null;
  public_ui?: string | null;
  domain?: string | null;
  known?: boolean;
  local_owner?: boolean;
  kind?: string | null;
  ownership?: string | null;
};

export function LiveTenantPicker({
  tenants,
  value,
  onChange,
}: {
  tenants: LiveTenantOption[];
  value: string;
  onChange: (slug: string) => void;
}) {
  const { t } = useTranslation();

  const ours = tenants.filter((tn) => tn.local_owner || tn.kind === 'ours');
  const fresh = tenants.filter((tn) => tn.slug === '__new__' || tn.kind === 'new_org');
  const foreign = tenants.filter((tn) => tn.kind === 'foreign' && tn.slug !== '__new__');

  const groups: Array<
    { key: string; label: string; items: LiveTenantOption[] }
  > = [
    {
      key: 'ours',
      label: t('liveDeploy.tenantGroupOurs', 'Naše organizace'),
      items: ours,
    },
    {
      key: 'new',
      label: t('liveDeploy.tenantGroupNew', 'Založit novou'),
      items: fresh,
    },
    {
      key: 'foreign',
      label: t('liveDeploy.tenantGroupForeign', 'Cizí existující'),
      items: foreign,
    },
  ].filter((g) => g.items.length > 0);

  return (
    <div className='w-full space-y-2'>
      {groups.map((g) => (
        <div key={g.key} className='space-y-1'>
          <p className='text-[10px] uppercase tracking-wide text-gray-600'>
            {g.label}
          </p>
          {g.items.map((tn) => {
            const selected = value === tn.slug;
            const beacon = tn.local_owner
              ? t('liveDeploy.beaconLocal', 'local')
              : (tn.beacon_url && tn.beacon_url !== 'local' ? tn.beacon_url : tn.public_ui) || null;
            const ca = tn.local_owner || tn.kind === 'ours'
              ? t('liveDeploy.caSubca', 'Sub-CA pro jméno nodu')
              : tn.kind === 'new_org' || tn.slug === '__new__'
              ? t('liveDeploy.caRoot', 'Root CA')
              : t('liveDeploy.caForward', 'jejich beacon / delegovaná Sub-CA');
            return (
              <button
                key={tn.slug}
                type='button'
                onClick={() => onChange(tn.slug)}
                className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                  selected
                    ? 'bg-blue-950/40 border-blue-500/40'
                    : 'bg-gray-950/40 border-gray-800 hover:border-gray-600'
                }`}
              >
                <div className='flex items-center gap-2'>
                  <Building2
                    size={14}
                    className={selected ? 'text-blue-300' : 'text-gray-500'}
                  />
                  <span className='text-sm font-semibold text-white'>
                    {tn.name}
                  </span>
                  {tn.slug !== '__new__' && (
                    <span className='font-mono text-[11px] text-gray-500'>
                      {tn.slug}
                    </span>
                  )}
                  <Badge className='text-[9px] ml-auto'>{ca}</Badge>
                </div>
                <div className='mt-0.5 pl-6 text-[11px] text-gray-400 flex flex-wrap gap-x-3 gap-y-0.5'>
                  {tn.domain && <span>{tn.domain}</span>}
                  {beacon
                    ? (
                      <span className='inline-flex items-center gap-1 font-mono text-blue-300/90'>
                        <Radio size={10} />
                        {beacon}
                      </span>
                    )
                    : tn.known === false
                    ? (
                      <span className='text-amber-400/80'>
                        {t('liveDeploy.tenantBeaconUnknown', 'beacon neznámý')}
                      </span>
                    )
                    : null}
                </div>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
